import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, IconButton, Snackbar, Alert, Grid, Card, CardContent,
  TablePagination, Tooltip, LinearProgress, InputAdornment, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  Note as NoteIcon,
  Tag as TagIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

const FinanzasAbonos = () => {
  const [quotations, setQuotations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Formulario de nuevo abono
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    // Fecha LOCAL, no UTC -- toISOString() adelanta el día después de ~18:00 en México (UTC-6).
    paymentDate: new Date().toLocaleDateString('en-CA'),
    paymentMethod: 'Transferencia',
    referenceNumber: '',
    notes: ''
  });

  // Errores de validación del formulario
  const [formErrors, setFormErrors] = useState({});

  // Cargar cotizaciones aceptadas
  useEffect(() => {
    loadAcceptedQuotations();
  }, []);

  const loadAcceptedQuotations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/quotations/payment-summary');
      setQuotations(response.data);
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
      setSnackbar({ open: true, message: 'Error al cargar cotizaciones', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateTotals = async () => {
    if (!window.confirm('¿Está seguro de recalcular los totales de todas las cotizaciones? Esto actualizará los montos basándose en los servicios registrados.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/quotations/recalculate-totals');
      setSnackbar({ open: true, message: response.data || 'Totales recalculados exitosamente', severity: 'success' });
      // Recargar las cotizaciones para mostrar los nuevos totales
      await loadAcceptedQuotations();
    } catch (error) {
      console.error('Error al recalcular totales:', error);
      setSnackbar({ open: true, message: 'Error al recalcular totales', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPaymentDialog = (quotation) => {
    setSelectedQuotation(quotation);
    setPaymentForm({
      amount: '',
      // Fecha LOCAL, no UTC -- toISOString() adelanta el día después de ~18:00 en México (UTC-6).
      paymentDate: new Date().toLocaleDateString('en-CA'),
      paymentMethod: 'Transferencia',
      referenceNumber: '',
      notes: ''
    });
    setFormErrors({});
    setOpenPaymentDialog(true);
  };

  const handleOpenHistoryDialog = async (quotation) => {
    setSelectedQuotation(quotation);
    setLoading(true);
    try {
      const response = await api.get(`/quotations/${quotation.quotationId}/payments`);
      setPayments(response.data);
      setOpenHistoryDialog(true);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setSnackbar({ open: true, message: 'Error al cargar historial de pagos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Validación en tiempo real del monto
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Solo permitir números y punto decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Evitar múltiples puntos decimales
    const parts = numericValue.split('.');
    const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
    
    setPaymentForm({ ...paymentForm, amount: sanitized });

    const errors = { ...formErrors };
    const amount = parseFloat(sanitized);
    if (sanitized && amount <= 0) {
      errors.amount = 'El monto debe ser mayor a $0';
    } else if (sanitized && amount > selectedQuotation?.totalAmount) {
      errors.amount = `El monto no puede exceder el total de la cotización (${formatCurrency(selectedQuotation?.totalAmount)})`;
    } else if (sanitized && amount > selectedQuotation?.remainingBalance) {
      errors.amount = `El monto no puede exceder el saldo pendiente (${formatCurrency(selectedQuotation?.remainingBalance)})`;
    } else {
      delete errors.amount;
    }
    setFormErrors(errors);
  };

  // Validación de referencia (máx 25 caracteres)
  const handleReferenceChange = (e) => {
    const value = e.target.value;
    if (value.length <= 25) {
      setPaymentForm({ ...paymentForm, referenceNumber: value });
    }
  };

  // Validación de notas (máx 100 caracteres)
  const handleNotesChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setPaymentForm({ ...paymentForm, notes: value });
    }
  };

  const validateForm = () => {
    const errors = {};
    const amount = parseFloat(paymentForm.amount);

    if (!paymentForm.amount || isNaN(amount)) {
      errors.amount = 'El monto es requerido';
    } else if (amount <= 0) {
      errors.amount = 'El monto debe ser mayor a $0';
    } else if (amount > selectedQuotation?.totalAmount) {
      errors.amount = `El monto no puede exceder el total de la cotización (${formatCurrency(selectedQuotation?.totalAmount)})`;
    } else if (amount > selectedQuotation?.remainingBalance) {
      errors.amount = `El monto no puede exceder el saldo pendiente (${formatCurrency(selectedQuotation?.remainingBalance)})`;
    }

    if (!paymentForm.paymentDate) {
      errors.paymentDate = 'La fecha de pago es requerida';
    }

    if (!paymentForm.paymentMethod) {
      errors.paymentMethod = 'Seleccione un método de pago';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePayment = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post(`/quotations/${selectedQuotation.quotationId}/payments`, paymentForm);
      setSnackbar({ open: true, message: 'Abono registrado exitosamente', severity: 'success' });
      setOpenPaymentDialog(false);
      loadAcceptedQuotations();
    } catch (error) {
      console.error('Error al guardar abono:', error);
      setSnackbar({ open: true, message: 'Error al registrar el abono', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusChip = (status) => {
    const statusConfig = {
      'SIN_PAGO': { label: 'Sin Pago', color: 'error', icon: <WarningIcon sx={{ fontSize: 16 }} /> },
      'PARCIAL': { label: 'Pago Parcial', color: 'warning', icon: <PaymentIcon sx={{ fontSize: 16 }} /> },
      'PAGADO': { label: 'Pagado', color: 'success', icon: <CheckIcon sx={{ fontSize: 16 }} /> }
    };
    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" icon={config.icon} variant="outlined" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  // paymentDate/lastPaymentDate llegan como "YYYY-MM-DD" (LocalDate, sin hora) -- `new
  // Date(str)` lo interpreta como medianoche UTC y en huso horario de México (UTC-6) muestra
  // un día antes.
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [y, m, d] = dateString.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-MX');
  };

  // Calcular porcentaje de progreso de pago
  const getPaymentProgress = (quotation) => {
    if (!quotation.totalAmount || quotation.totalAmount === 0) return 0;
    return Math.min(((quotation.totalPaid || 0) / quotation.totalAmount) * 100, 100);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
          borderRadius: 3,
          color: '#fff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WalletIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Gestión de Abonos
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Administra los pagos y abonos de las cotizaciones aceptadas
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={handleRecalculateTotals}
            disabled={loading}
            sx={{
              backgroundColor: '#fff',
              color: '#1a237e',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            🔄 Recalcular Totales
          </Button>
        </Box>
      </Paper>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={2} sx={{ borderRadius: 3, borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ReceiptIcon sx={{ color: '#1976d2', fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Cotizaciones
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                    {quotations.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={2} sx={{ borderRadius: 3, borderLeft: '4px solid #ed6c02' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WarningIcon sx={{ color: '#ed6c02', fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Con Saldo Pendiente
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                    {quotations.filter(q => q.paymentStatus !== 'PAGADO').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={2} sx={{ borderRadius: 3, borderLeft: '4px solid #2e7d32' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Pagadas
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    {quotations.filter(q => q.paymentStatus === 'PAGADO').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={2} sx={{ borderRadius: 3, borderLeft: '4px solid #d32f2f' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <MoneyIcon sx={{ color: '#d32f2f', fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Saldo Total Pendiente
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                    {formatCurrency(quotations.reduce((sum, q) => sum + (q.remainingBalance || 0), 0))}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de cotizaciones */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Folio</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Cliente</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#37474f' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#37474f' }}>Pagado</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#37474f', minWidth: 180 }}>Progreso</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#37474f' }}>Saldo Pendiente</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Último Pago</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#37474f' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((quotation) => (
                  <TableRow 
                    key={quotation.quotationId} 
                    hover
                    sx={{ '&:hover': { backgroundColor: '#f5f8ff' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e' }}>
                        {quotation.invoiceFolio || `COT-${quotation.quotationId}`}
                      </Typography>
                    </TableCell>
                    <TableCell>{quotation.clientName}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(quotation.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                        {formatCurrency(quotation.totalPaid)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={getPaymentProgress(quotation)} 
                          sx={{ 
                            flexGrow: 1, 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              backgroundColor: getPaymentProgress(quotation) >= 100 ? '#2e7d32' : '#1976d2'
                            }
                          }} 
                        />
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 35 }}>
                          {Math.round(getPaymentProgress(quotation))}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        sx={{ 
                          fontWeight: 700,
                          color: quotation.remainingBalance > 0 ? '#d32f2f' : '#2e7d32'
                        }}
                      >
                        {formatCurrency(quotation.remainingBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getPaymentStatusChip(quotation.paymentStatus)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {quotation.lastPaymentDate
                          ? formatDate(quotation.lastPaymentDate)
                          : 'Sin pagos'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Historial de Pagos">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenHistoryDialog(quotation)}
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {quotation.remainingBalance > 0 && (
                        <Tooltip title="Registrar Abono">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenPaymentDialog(quotation)}
                            sx={{ 
                              color: '#2e7d32',
                              '&:hover': { backgroundColor: '#e8f5e9' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {quotations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <ReceiptIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No hay cotizaciones aceptadas
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={quotations.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página:"
        />
      </Paper>

      {/* Dialog: Registrar Abono */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={() => setOpenPaymentDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #43a047 100%)',
            color: '#fff',
            py: 2.5,
            px: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MoneyIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Registrar Nuevo Abono
                </Typography>
                {selectedQuotation && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedQuotation.invoiceFolio || `COT-${selectedQuotation.quotationId}`} - {selectedQuotation.clientName}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={() => setOpenPaymentDialog(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Grid container spacing={2.5}>
            {/* Resumen financiero */}
            <Grid size={{ xs: 12 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  border: '1px solid #e0e0e0',
                  background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                }}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Cotización
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#37474f' }}>
                      {formatCurrency(selectedQuotation?.totalAmount)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Pagado
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {formatCurrency(selectedQuotation?.totalPaid)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Saldo Pendiente
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                      {formatCurrency(selectedQuotation?.remainingBalance)}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={selectedQuotation ? getPaymentProgress(selectedQuotation) : 0} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: '#2e7d32'
                      }
                    }} 
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>

            {/* Monto del abono */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Monto del Abono *"
                value={paymentForm.amount}
                onChange={handleAmountChange}
                error={!!formErrors.amount}
                helperText={formErrors.amount || `Máximo: ${formatCurrency(selectedQuotation?.remainingBalance)}`}
                placeholder="0.00"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon sx={{ color: formErrors.amount ? '#d32f2f' : '#2e7d32', fontSize: 20 }} />
                      $
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: formErrors.amount ? '#d32f2f' : '#2e7d32'
                    }
                  }
                }}
              />
            </Grid>

            {/* Fecha de pago */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Fecha de Pago *"
                type="date"
                value={paymentForm.paymentDate}
                onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                error={!!formErrors.paymentDate}
                helperText={formErrors.paymentDate}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            {/* Método de pago */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" error={!!formErrors.paymentMethod}>
                <InputLabel>Método de Pago *</InputLabel>
                <Select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  label="Método de Pago *"
                  startAdornment={
                    <InputAdornment position="start">
                      <PaymentIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                    </InputAdornment>
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Efectivo">Efectivo</MenuItem>
                  <MenuItem value="Transferencia">Transferencia Bancaria</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Tarjeta">Tarjeta de Crédito/Débito</MenuItem>
                  <MenuItem value="Deposito">Depósito Bancario</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Número de referencia */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Número de Referencia"
                value={paymentForm.referenceNumber}
                onChange={handleReferenceChange}
                placeholder="Ej: REF-123456"
                size="small"
                helperText={`${paymentForm.referenceNumber.length}/25 caracteres`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TagIcon sx={{ color: '#757575', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* Notas */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notas"
                multiline
                rows={2}
                value={paymentForm.notes}
                onChange={handleNotesChange}
                placeholder="Observaciones adicionales sobre el pago..."
                size="small"
                helperText={`${paymentForm.notes.length}/100 caracteres`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <NoteIcon sx={{ color: '#757575', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setOpenPaymentDialog(false)} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSavePayment} 
            variant="contained" 
            disabled={loading || !!formErrors.amount}
            startIcon={<MoneyIcon />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none', 
              fontWeight: 600,
              backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20' }
            }}
          >
            {loading ? 'Registrando...' : 'Registrar Abono'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Historial de Pagos */}
      <Dialog 
        open={openHistoryDialog} 
        onClose={() => setOpenHistoryDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
            color: '#fff',
            py: 2.5,
            px: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ViewIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Historial de Abonos
                </Typography>
                {selectedQuotation && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedQuotation.invoiceFolio || `COT-${selectedQuotation.quotationId}`} - {selectedQuotation.clientName}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={() => setOpenHistoryDialog(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedQuotation && (
            <Box sx={{ p: 2.5, backgroundColor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">Total Cotización</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#37474f' }}>
                    {formatCurrency(selectedQuotation.totalAmount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">Total Pagado</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    {formatCurrency(selectedQuotation.totalPaid)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">Saldo Pendiente</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                    {formatCurrency(selectedQuotation.remainingBalance)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Monto</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Método</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Registrado por</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment, index) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <Chip label={index + 1} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={payment.paymentMethod} size="small" variant="outlined" color="primary" />
                    </TableCell>
                    <TableCell>{payment.referenceNumber || '-'}</TableCell>
                    <TableCell>{payment.createdByName || `Usuario #${payment.createdBy}`}</TableCell>
                  </TableRow>
                ))}
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <PaymentIcon sx={{ fontSize: 40, color: '#bdbdbd', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No hay abonos registrados para esta cotización
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setOpenHistoryDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinanzasAbonos;
